import { t } from '@lingui/macro'
import {
    Address,
    AirportCode,
    CalculatedRoute,
    ConvertedShipment,
    DataQualityScore,
    Distance,
    DistanceCalculationDetails,
    DistanceCalculationMethod,
    EmissionFactorWithGasEmissions,
    EstimateMassUnit,
    InferenceDetails,
    Load,
    Locode,
    LogisticsSiteMethod,
    MethodologyDetails as LuneMethodologyDetails,
    NullEnum,
    RoadEmissionStandard,
    RoadGradient,
    RoadMethodologyDetails,
    RoadShippingMethod,
    RoadSituation,
    RouteInferenceDetails,
    RouteInferenceExplanation,
    RouteInferenceLegExplanation,
    SeaMethodologyDetails,
    ShipmentConversionMethod,
    ShippingMethod,
    ShippingRoute,
    ShippingSourceDestination,
    VesselInferenceMethod,
    VesselInferenceMethodDetails,
} from '@lune-climate/lune'
import { Text, TooltipList } from '@lune-fe/lune-ui-lib'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'
import OilBarrelOutlinedIcon from '@mui/icons-material/OilBarrelOutlined'
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined'
import { Box } from '@mui/material'
import Big from 'big.js'
import { JSX, useMemo } from 'react'
import { None, Some } from 'ts-results-es'

import { useLuneI18nLocale } from '../../hooks'

import {
    type DisplayMassUnit,
    formatNumbers,
    getCountryNameByThreeLetterCode,
    optionFromNullable,
    toTitleCase,
} from './../../utils/utils'
import {
    formattedValue,
    getDistanceCalculationMethodType,
    getDistanceExplanationText,
} from './utils/explanationUtils'
import {
    formatAddress,
    getCapacityUtilisationLabel,
    getEmptyRunsFactorLabel,
    getLoadFactorLabel,
    getRoadEmissionStandard,
    getRoadGrandientAndSituationLabel,
    getRoadVehicleName,
    parseVesselInferenceDetails,
} from './utils/logisticsUtils'
import ExplanationChip from './ExplanationChip'
import { ExplanationStep, ExplanationText } from './ExplanationSteps'
import { getSourceOrDestination } from './utils'

type EmissionsValue = {
    amount: string
    unit: EstimateMassUnit
}

export interface EmissionsExplanationSectionProps {
    emissions: EmissionsValue & {
        wtwDetails?: { wtt: EmissionsValue; ttw: EmissionsValue; unknown: EmissionsValue } | null
        pollutants?: {
            nox: { wtt: EmissionsValue; ttw: EmissionsValue }
            nmhc: { wtt: EmissionsValue; ttw: EmissionsValue }
            pm: { wtt: EmissionsValue; ttw: EmissionsValue }
            co2: { wtt: EmissionsValue; ttw: EmissionsValue }
            so2: { wtt: EmissionsValue; ttw: EmissionsValue }
        } | null
    }
    /**
     * If `responseRoute` is non-null then this property has to be non-null as well.
     */
    rawDistance: Distance | undefined
    distance: Distance | undefined
    adjustedDistance: Distance | undefined // Deprecated
    load: Load
    distanceCalculationMethod: DistanceCalculationMethod | NullEnum
    distanceCalculationDetails: DistanceCalculationDetails | NullEnum
    convertedLoad: ConvertedShipment | NullEnum
    loadConversionMethod: ShipmentConversionMethod | NullEnum
    methodology: string[]
    emissionFactor: EmissionFactorWithGasEmissions
    /**
     * If `responseRoute` is non-null then this property has to be non-null as well.
     */
    requestRoute: ShippingRoute | null
    requestMethod: ShippingMethod | LogisticsSiteMethod
    responseRoute: CalculatedRoute | null
    methodologyDetails:
        | MethodologyDetails
        | RoadMethodologyDetails
        | ElectricMethodMethodologyDetails
        | RouteInferenceDetails
        | SeaMethodologyDetails
        | null
    vesselInference: VesselInferenceMethod | null
    mapboxAccessToken?: string
    hideEmissionFactorUrl?: boolean
    emissionsUnit?: DisplayMassUnit
    pdfStructure?: boolean
    dataQualityScore?: DataQualityScore | null
    legIndex?: string
    inferenceDetails?: InferenceDetails | null
    routeInferenceLegExplanation?: RouteInferenceLegExplanation | null
}

export type MethodologyDetails = {
    flight?: FlightMethodologyDetails
}

export type ElectricMethodMethodologyDetails = {
    electricityConsumption: {
        amount: string
        unit: 'kWh/tkm'
    }
}

export type FlightMethodologyDetails = {
    aircraftName: string
    aircraftConfiguration: LuneMethodologyDetails.aircraft_configuration
    aircraftCapacityTonnes: {
        passenger: number
        cargo: number
    }
    loadFactor: {
        passenger: number
        cargo: number
    }
    flightFuelConsumedTonnes: number
    distanceAdjustmentFactor: number
    emissionShare: number
}

const InferenceChip = ({ text }: { text: string }) => {
    return <ExplanationChip text={text} sx={{ color: 'Blue700', backgroundColor: '#EFF5FF' }} />
}

function pickDistanceCalculationDetails(
    distanceCalculationDetails: DistanceCalculationDetails | NullEnum,
): string | null {
    if (distanceCalculationDetails === null) {
        return null
    }

    if ('vesselTracking' in distanceCalculationDetails) {
        if (distanceCalculationDetails.vesselTracking === null) {
            return null
        }
        if ('message' in distanceCalculationDetails.vesselTracking) {
            return distanceCalculationDetails.vesselTracking.message
        }
        if ('adjustments' in distanceCalculationDetails.vesselTracking) {
            return distanceCalculationDetails.vesselTracking.adjustments.join('. ')
        }
    }

    return null
}

export const getRoadLoadExplanations = (
    method: RoadShippingMethod,
    methodologyDetails: RoadMethodologyDetails,
): ExplanationStep => {
    const chips = [
        <ExplanationChip
            key={`key-payload-capacity`}
            text={`${methodologyDetails.vehicleCapacity} t payload capacity`}
        />,
    ]
    if (!method.loadFactor) {
        chips.push(
            <ExplanationChip
                key={`key-load-factor`}
                text={getLoadFactorLabel(methodologyDetails.loadFactor)}
            />,
        )
    }
    if (!method.emptyRunsFactor) {
        chips.push(
            <ExplanationChip
                key={`key-empty-runs-factor`}
                text={getEmptyRunsFactorLabel(methodologyDetails.emptyRunsFactor)}
            />,
        )
    }

    const elements = chips.reduce(
        (result: React.ReactNode[], node: React.ReactNode, index: number) => {
            if (index === 0) {
                return [node]
            }
            if (index === chips.length - 1) {
                return [...result, <ExplanationText key={`key-and`} text={t`and`} />, node]
            }
            return [...result, <ExplanationText key={`key-comma-${index}`} text="," />, node]
        },
        [],
    )

    return {
        description: (
            <>
                <ExplanationText text={t`Assumed`} />
                {elements}
            </>
        ),
    }
}

export const getElectricEnergyConsumptionExplanation = (
    methodologyDetails: ElectricMethodMethodologyDetails,
): ExplanationStep => {
    // if there's a slash, add a space before and after, otherwise nothing is replaced
    const unit = methodologyDetails.electricityConsumption.unit.replace(/(\w+)\/(\w+)/, '$1 / $2')
    return {
        description: (
            <>
                <ExplanationText text={t`Calculated an electricity consumption of`} />
                <ExplanationChip
                    text={`${Big(methodologyDetails.electricityConsumption.amount).round(4)} ${unit}`}
                />
            </>
        ),
        info: (
            <>
                <Text variant={'caption'} component={'p'} sx={{ color: 'white' }}>
                    Calculation based on GLEC methodology.
                </Text>
            </>
        ),
    }
}

export const getRoadEnergyConsuptionExplanation = (
    method: RoadShippingMethod,
    methodologyDetails: RoadMethodologyDetails,
): ExplanationStep => {
    // if there's a slash, add a space before and after, otherwise nothing is replaced
    const unit = methodologyDetails.energyConsumption.unit.replace(/(\w+)\/(\w+)/, '$1 / $2')
    return {
        description: (
            <>
                <ExplanationText text={t`Calculated a vehicle energy consumption of`} />
                <ExplanationChip
                    text={`${Big(methodologyDetails.energyConsumption.amount).round(4)} ${unit}`}
                />
            </>
        ),
        info: (
            <>
                <Text variant={'caption'} component={'p'} sx={{ color: 'white' }}>
                    Calculation based on HBEFA data:
                </Text>
                <TooltipList
                    items={[
                        getRoadVehicleName(method.vehicleType),
                        getCapacityUtilisationLabel(methodologyDetails.capacityUtilisation),
                        getRoadEmissionStandard(methodologyDetails.emissionStandard),
                        getRoadGrandientAndSituationLabel(
                            methodologyDetails.gradient,
                            methodologyDetails.situation,
                        ),
                    ]}
                />
            </>
        ),
    }
}

export const getRoadRefrigerationExplanation = (
    methodologyDetails: EmissionsExplanationSectionProps['methodologyDetails'],
): ExplanationStep | null => {
    if (
        methodologyDetails &&
        'refrigerationFactor' in methodologyDetails &&
        !!methodologyDetails.refrigerationFactor
    ) {
        return {
            description: (
                <>
                    <ExplanationText text={t`Applied a`} />{' '}
                    <ExplanationChip
                        text={`${Big(methodologyDetails.refrigerationFactor).minus(1).times(100)}%`}
                    />{' '}
                    <ExplanationText text={t`refrigeration uplift`} />
                </>
            ),
            info: (
                <Text variant="caption" component="p" sx={{ color: 'white' }}>
                    As per GLEC framework guidelines
                </Text>
            ),
        }
    }
    return null
}

export const getAircraftTypeExplanation = (
    requestMethod: ShippingMethod | LogisticsSiteMethod,
    flightDetails: FlightMethodologyDetails,
): ExplanationStep => {
    if (
        !(
            typeof requestMethod === 'object' &&
            ('flightNumber' in requestMethod || 'aircraftType' in requestMethod)
        )
    ) {
        throw new Error('Flight number or Aircraft type in an expected request input')
    }
    return 'flightNumber' in requestMethod
        ? {
              description: (
                  <>
                      <ExplanationText text={t`Identified`} />
                      <ExplanationChip text={flightDetails.aircraftName} />
                      <ExplanationText text={t`from flight number`} />
                      <ExplanationChip text={requestMethod.flightNumber} />
                  </>
              ),
          }
        : {
              description: (
                  <>
                      <ExplanationText text={t`Selected`} />
                      <ExplanationChip text={flightDetails.aircraftName} />
                      <ExplanationText text={t`as aircraft type`} />
                  </>
              ),
          }
}

const getFuelUsageExplanation = (flightDetails: FlightMethodologyDetails): ExplanationStep => {
    const fuelUsed = flightDetails.flightFuelConsumedTonnes * flightDetails.emissionShare
    return {
        description: (
            <>
                <ExplanationText text={t`Calculated the shipment's fuel usage share as`} />
                <ExplanationChip text={`${formatNumbers(fuelUsed, 2)} t`} />
            </>
        ),
        info: (
            <>
                <Text variant={'caption'} component={'p'} sx={{ color: 'white' }}>
                    Capacity:{' '}
                    {flightDetails.aircraftCapacityTonnes.cargo +
                        flightDetails.aircraftCapacityTonnes.passenger}{' '}
                    tonnes
                </Text>
                <Text variant={'caption'} component={'p'} sx={{ color: 'white' }}>
                    Configuration: {flightDetails.aircraftConfiguration.replaceAll('_', ' ')}
                </Text>
                <Text variant={'caption'} component={'p'} sx={{ color: 'white' }}>
                    Load factor: {Big(flightDetails.loadFactor.cargo).mul(100).round(2).toString()}%
                </Text>
            </>
        ),
    }
}

const getAirShipmentDistanceInfo = (
    distance: Distance | undefined,
    adjustedDistance: Distance | undefined,
): string | undefined => {
    if (!distance || !adjustedDistance) {
        return
    }
    const DAF = Number(adjustedDistance.amount) - Number(distance.amount)
    return `Including a Distance Adjustment Factor (DAF) of ${DAF} km`
}

const airShipmentFormulaExplanation: ExplanationStep = {
    description: (
        <ExplanationText
            text={t`Multiplied the Emission Factor by the shipment's fuel usage share`}
        />
    ),
}

const getRouteNodesNames = (route: CalculatedRoute): string[] => {
    return route.legs
        .map(({ location: { label } }) => label)
        .filter((label): label is string => label !== null)
}

export const filterStops = (nodeNames: string[]): string[] => {
    const locodeRegex = /\(LOCODE [A-Z]{2}[A-Z0-9]{3}\)/
    return nodeNames.filter((name) => locodeRegex.test(name))
}

export const formatStop = (name: string): string => {
    const regex = /^([^,]+).*\(LOCODE ([A-Z]{2}[A-Z0-9]{3})\)/
    const match = name.match(regex)
    if (match) {
        const location = match[1].toLowerCase()
        const locode = match[2]
        const formattedLocation = location.trim().split(' ').map(toTitleCase).join(' ')
        return `${formattedLocation} ${locode}`
    }
    return name
}

const filterAndTransformStops = (nodeNames: string[]): string[] => {
    return filterStops(nodeNames).map(formatStop)
}

export const formatCarrierScheduleStopsTooltipSubject = (s: string): React.ReactNode => {
    const regex = /(.*?)\s*(Journey.*)/
    const result = s.match(regex)
    if (result) {
        const [, part1, part2] = result
        return (
            <>
                {part1}
                <br />
                {part2}
            </>
        )
    }
    // default to showing the string as-is
    return <>{s}</>
}

const getCarrierSchedulesStopTooltip = ({
    subject,
    stops,
}: {
    subject: string
    stops: string[]
}): JSX.Element => {
    return (
        <>
            {formatCarrierScheduleStopsTooltipSubject(subject)}
            {stops.length > 0 && <TooltipList items={stops} />}
        </>
    )
}

// eslint-disable-next-line complexity
const getTooltipMessage = ({
    distanceCalculationMethod,
    distanceCalculationDetails,
    distance,
    adjustedDistance,
    formattedDistance,
    responseRoute,
    vesselInference,
}: {
    distanceCalculationMethod: DistanceCalculationMethod
    distanceCalculationDetails: DistanceCalculationDetails | null
    distance: Distance | undefined
    adjustedDistance: Distance | undefined
    formattedDistance: number | string
    responseRoute: CalculatedRoute | null
    vesselInference: VesselInferenceMethod | null
}): JSX.Element | string | null => {
    if (
        distanceCalculationMethod === DistanceCalculationMethod.CARRIER_SCHEDULE_STOPS &&
        responseRoute &&
        distanceCalculationDetails?.carrierScheduleStops?.message
    ) {
        return getCarrierSchedulesStopTooltip({
            subject: distanceCalculationDetails.carrierScheduleStops.message,
            stops: filterAndTransformStops(getRouteNodesNames(responseRoute)),
        })
    }

    if (distanceCalculationMethod === 'great_circle_distance') {
        return getAirShipmentDistanceInfo(distance, adjustedDistance) ?? null
    }
    const vesselInferenceTooltip =
        vesselInference && 'error' in vesselInference ? vesselInference.error : null

    const distanceTooltip =
        distance && distance.amount !== adjustedDistance?.amount
            ? `We calculated a Shortest Feasible Distance of ${formattedDistance} km, then adjusted by +15%, as per GLEC guidelines for container ships.`
            : null

    if (vesselInferenceTooltip === null && distanceTooltip === null) {
        return null
    }
    if (vesselInferenceTooltip !== null && distanceTooltip !== null) {
        return (
            <>
                {vesselInferenceTooltip}
                <br />
                {distanceTooltip}
            </>
        )
    }
    return vesselInferenceTooltip ?? distanceTooltip
}

type TransitCorridor = NonNullable<ShippingSourceDestination['transitCorridor']>

function isRouteWithTransitCorridor(
    route: ShippingRoute | null,
): route is ShippingSourceDestination & {
    transitCorridor: TransitCorridor
} {
    return route !== null && 'transitCorridor' in route && route.transitCorridor !== undefined
}

export function formatTransitCorridor(transitCorridor: TransitCorridor): string {
    return transitCorridor.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
}

export const getDistanceExplanation = (
    distance: Distance | undefined,
    adjustedDistance: Distance | undefined,
    distanceCalculationMethod: DistanceCalculationMethod | NullEnum,
    distanceCalculationDetails: DistanceCalculationDetails | NullEnum,
    responseRoute: CalculatedRoute | null,
    vesselInference: VesselInferenceMethod | null,
    requestRoute: ShippingRoute | null,
): ExplanationStep => {
    if (
        !distance ||
        !distanceCalculationMethod ||
        distanceCalculationMethod === DistanceCalculationMethod.USER_INPUT
    ) {
        return { description: undefined }
    }

    const formattedDistance = formatNumbers(distance.amount, 2)
    const formattedAdjustedDistance = formatNumbers(adjustedDistance!.amount, 2)

    const step = (
        <>
            <ExplanationText
                text={t`Calculated ${getDistanceCalculationMethodType(distanceCalculationMethod)} of`}
            />
            <ExplanationChip text={formattedAdjustedDistance + ' km'} />
            <ExplanationText text={t`using`} />
            <ExplanationChip text={getDistanceExplanationText(distanceCalculationMethod)} />
            {isRouteWithTransitCorridor(requestRoute) &&
            distanceCalculationMethod === DistanceCalculationMethod.SEA_DISTANCE_ALGORITHM ? (
                <>
                    <ExplanationText text={t`via`} />
                    <ExplanationChip text={formatTransitCorridor(requestRoute.transitCorridor)} />
                    <ExplanationText text={t`transit corridor`} />
                </>
            ) : undefined}
        </>
    )
    const vesselTrackingStep = (
        <>
            <ExplanationText
                text={t`Tracked ${getDistanceCalculationMethodType(distanceCalculationMethod)} for`}
            />
            <ExplanationChip text={formattedDistance + ' km'} />
            <ExplanationText text={t`using`} />
            <ExplanationChip text={t`AIS data`} />
        </>
    )
    const carrierScheduleStep = (
        <>
            <ExplanationText
                text={t`Calculated ${getDistanceCalculationMethodType(distanceCalculationMethod)} of`}
            />
            <ExplanationChip text={formattedAdjustedDistance + ' km'} />
            <ExplanationText text={t`using`} />
            <ExplanationChip
                text={getDistanceExplanationText(DistanceCalculationMethod.SEA_DISTANCE_ALGORITHM)}
            />
            <ExplanationText text={t`and`} />
            <ExplanationChip text={t`carrier schedule`} />
        </>
    )

    const tooltipMessage = getTooltipMessage({
        distanceCalculationMethod,
        distanceCalculationDetails,
        distance,
        adjustedDistance,
        formattedDistance,
        responseRoute,
        vesselInference,
    })

    return {
        description:
            distanceCalculationMethod === DistanceCalculationMethod.VESSEL_TRACKING
                ? vesselTrackingStep
                : distanceCalculationMethod === DistanceCalculationMethod.CARRIER_SCHEDULE_STOPS
                  ? carrierScheduleStep
                  : step,
        info: tooltipMessage ?? pickDistanceCalculationDetails(distanceCalculationDetails ?? null),
    }
}

const getShipmentExplanation = (
    load: Load,
    convertedLoad: ConvertedShipment | NullEnum,
    loadConversionMethod: ShipmentConversionMethod | NullEnum,
): ExplanationStep => {
    const formattedLoad =
        'containers' in load
            ? `${formattedValue(load.containers)} TEU`
            : `${formattedValue(load.mass.amount)} ${load.mass.unit}`
    const formattedConvertedShipment = convertedLoad
        ? `${formattedValue(convertedLoad.amount)} ${
              convertedLoad.unit === 'TEU' ? ' TEU' : convertedLoad.unit
          }`
        : ''

    if (loadConversionMethod === ShipmentConversionMethod.USER_PROVIDED_CARGO_TYPE) {
        return {
            description: (
                <>
                    <ExplanationText text={t`Converted`} />
                    <ExplanationChip text={formattedLoad} testId={'converted-load'} />
                    <ExplanationText text={t`to`} />
                    <ExplanationChip text={formattedConvertedShipment} />
                    <ExplanationText
                        text={`given user-inputted cargo type, to match the Emission Factor's unit`}
                    />
                </>
            ),
        }
    }
    if (loadConversionMethod === ShipmentConversionMethod.AVERAGE_CARGO_TYPE) {
        return {
            description: (
                <>
                    <ExplanationText text={t`Converted`} />
                    <ExplanationChip text={formattedLoad} testId={'converted-load'} />
                    <ExplanationText text={t`to`} />
                    <ExplanationChip text={formattedConvertedShipment} />
                    <ExplanationText
                        text={`assuming average cargo type, to match the Emission Factor's unit`}
                    />
                </>
            ),
        }
    }

    return { description: undefined }
}

const getFormulaExplanation = ({
    distance,
    isDetailedRoadEstimate,
    isElectricityDerivedMethod,
}: {
    distance: Distance | undefined
    isDetailedRoadEstimate: boolean
    isElectricityDerivedMethod: boolean
}): ExplanationStep => {
    if (isElectricityDerivedMethod) {
        return {
            description: (
                <ExplanationText
                    text={t`Multiplied the Emission Factor by the electricity consumption, distance and load`}
                />
            ),
        }
    }
    if (isDetailedRoadEstimate) {
        return {
            description: (
                <ExplanationText
                    text={t`Multiplied the Emission Factor by the energy consumption, distance and load`}
                />
            ),
        }
    }
    if (distance) {
        return {
            description: (
                <ExplanationText
                    text={t`Multiplied the Emission Factor by the distance and load`}
                />
            ),
        }
    }
    return {
        description: <ExplanationText text={t`Multiplied the Emission Factor by the load`} />,
    }
}

const getEmissionFactorExplanation = (
    methodology: string[],
    emissionFactor: EmissionFactorWithGasEmissions,
    options?: {
        basedOnInput?: boolean
    },
): ExplanationStep => {
    let emissionFactorMessage: string | null = null
    if (
        methodology.includes('imo_unavailable_container_ship_fallback') ||
        methodology.includes('imo_unavailable_container_ship_trade_lane_fallback')
    ) {
        emissionFactorMessage = t`We couldn't find an Emission Factor for the inputted vessel`
    }
    return {
        description: (
            <>
                <ExplanationText text={t`Selected the`} />{' '}
                <ExplanationChip text={emissionFactor.name} />
                <ExplanationText
                    text={
                        options?.basedOnInput
                            ? t`Emission Factor based on Lune's emission intelligence`
                            : t`Emission Factor`
                    }
                />
            </>
        ),
        info: emissionFactorMessage,
    }
}

function getInferredVesselExplanation(details: VesselInferenceMethodDetails): ExplanationStep {
    const vessel = parseVesselInferenceDetails(details.details)
        // eslint-disable-next-line react/jsx-key
        .map((vessel) => <InferenceChip text={vessel.formattedVessel} />)
        // A fallback in case the format of the vessel inference details changes and
        // we (temporarily) can't identify vessels.
        .unwrapOr(<ExplanationText text={t`the vessel`} />)
    return {
        description: (
            <>
                <ExplanationText text={t`Inferred`} /> {vessel}
                <ExplanationText text={t`from the provided carrier`} />
            </>
        ),
        stepIcon: 'intelligence',
    }
}

function getTradeLaneInferenceExplanation(tradeLane: string | undefined): ExplanationStep {
    if (tradeLane) {
        return {
            description: (
                <>
                    <ExplanationText text={t`Inferred`} />
                    <InferenceChip text={tradeLane} />
                    <ExplanationText text={t`trade lane from origin and destination locations`} />
                </>
            ),
            stepIcon: 'intelligence',
        }
    }

    return { description: undefined }
}

function getSnappedToTransferPointExplanation(
    snappedLocation: RouteInferenceExplanation['snappedSourceToKnownLocation'],
    sourceOrDestination: 'source' | 'destination',
): ExplanationStep {
    if (snappedLocation) {
        const inferencePoint =
            sourceOrDestination === 'source' ? t`start location` : t`end location`
        return {
            description: (
                <>
                    <ExplanationText text={t`Inferred`} />
                    <InferenceChip text={inferencePoint} />
                    <ExplanationText text={t`as`} />
                    <InferenceChip
                        text={`${snappedLocation.label ?? getSourceOrDestination(snappedLocation.coordinates, '').label}`}
                    />
                </>
            ),
            stepIcon: 'intelligence',
        }
    }

    return { description: undefined }
}

function getLLMCleanedRoute(
    route: Address | AirportCode | Locode | null,
    sourceOrDestination: 'source' | 'destination',
): ExplanationStep {
    const sourceDestinationLabel = sourceOrDestination === 'source' ? t`source` : t`destination`
    const formattedRoute =
        route !== null
            ? 'code' in route
                ? route.code
                : 'locode' in route
                  ? route.locode
                  : formatAddress(route)
            : undefined
    return {
        description: formattedRoute ? (
            <>
                <ExplanationText text={t`Inferred ${sourceDestinationLabel} as`} />
                <InferenceChip text={formattedRoute} />
            </>
        ) : undefined,
        stepIcon: 'intelligence',
    }
}

function getRailTypeInferenceExplanation(inferenceDetails: {
    railType: InferenceDetails.rail_type
    sourceCountry: string
    destinationCountry: string
}): ExplanationStep {
    const electricOrDiesel = inferenceDetails.railType.split('_').map(toTitleCase).join(' ')

    const internationalOrDomestic =
        inferenceDetails.sourceCountry === inferenceDetails.destinationCountry
            ? t`domestic`
            : t`international`

    const withinOrBetweenCountries =
        internationalOrDomestic === t`international`
            ? t`departing from ${getCountryNameByThreeLetterCode(inferenceDetails.sourceCountry)} and arriving in ${getCountryNameByThreeLetterCode(inferenceDetails.destinationCountry)}`
            : t`within ${getCountryNameByThreeLetterCode(inferenceDetails.sourceCountry)}`

    return {
        description: (
            <>
                <ExplanationText text={t`Inferred`} />
                <InferenceChip text={electricOrDiesel} />
                <ExplanationText
                    text={`based on ${internationalOrDomestic} travel ${withinOrBetweenCountries}`}
                />
            </>
        ),
        stepIcon: 'intelligence',
    }
}

function getRoadEmissionStandardInferenceExplanation(
    inferenceDetails: Exclude<InferenceDetails['roadEmissionStandard'], null>,
): ExplanationStep {
    const {
        from,
        sourceCountryCode,
        destinationCountryCode,
        sourceRegion,
        destinationRegion,
        value,
    } = inferenceDetails

    const resolveLocation = (region: string | null, countryCode: string | null): string | null => {
        if (region) {
            return region
        }
        if (countryCode) {
            const countryName = getCountryNameByThreeLetterCode(countryCode)
            return countryName ?? countryCode
        }
        // It may not be needed here but we had some crashes in getRoadFuelInferenceExplanation,
        // let's handle the potentially missing data here too.
        // https://linear.app/lune/issue/LUN-4907/dashboard-error-expected-region-or-country-code-to-be-defined-while
        return null
    }

    const isSameRegion = sourceRegion && destinationRegion && sourceRegion === destinationRegion
    const isSameCountry =
        sourceCountryCode && destinationCountryCode && sourceCountryCode === destinationCountryCode
    const noLocationInfo =
        sourceRegion === null &&
        sourceCountryCode === null &&
        destinationRegion === null &&
        destinationCountryCode === null

    const geographyParts = (() => {
        if (noLocationInfo || sourceRegion === 'World' || destinationRegion === 'World') {
            // If we fallback to a global factor, we prefer to say
            // "inferred from shipment route" rather than
            // "inferred from shipment route involving World"
            return { prefix: '', elements: <></> }
        }
        if (isSameRegion) {
            return { prefix: t`within`, elements: <InferenceChip text={sourceRegion} /> }
        }
        if (isSameCountry) {
            return {
                prefix: t`within`,
                elements: (
                    <InferenceChip text={getCountryNameByThreeLetterCode(sourceCountryCode)!} />
                ),
            }
        }

        const sourceLocation =
            (from === 'source' || from === 'both') &&
            resolveLocation(sourceRegion, sourceCountryCode)
        const destinationLocation =
            (from === 'destination' || from === 'both') &&
            resolveLocation(destinationRegion, destinationCountryCode)

        return {
            prefix: t`involving`,
            elements: (
                <>
                    {sourceLocation && <InferenceChip text={sourceLocation} />}
                    {sourceLocation && destinationLocation && (
                        <>
                            {' '}
                            <ExplanationText text={t`and`} />{' '}
                        </>
                    )}
                    {destinationLocation && <InferenceChip text={destinationLocation} />}
                </>
            ),
        }
    })()

    function formatRoadEmissionStandard(value: RoadEmissionStandard) {
        return `Euro ${value.slice(4)}`
    }

    return {
        description: (
            <>
                <ExplanationText text={t`Inferred`} />{' '}
                <InferenceChip text={formatRoadEmissionStandard(value)} />
                <ExplanationText
                    text={` emission standard from shipping route ${geographyParts.prefix}`}
                />
                {geographyParts.elements}
            </>
        ),
        stepIcon: 'intelligence',
    }
}

function getRoadVehicleTypeInferenceExplanation(
    inferenceDetails: Exclude<InferenceDetails['roadTruckType'], null>,
): ExplanationStep {
    const internationalOrDomestic = inferenceDetails.isInternationalShipment
        ? t`international`
        : t`domestic`

    return {
        description: (
            <>
                <ExplanationText text={t`Inferred`} />
                <InferenceChip text={getRoadVehicleName(inferenceDetails.value)} />
                <ExplanationText text={t`based on shipment payload and`} />
                <InferenceChip text={internationalOrDomestic} />
                <ExplanationText text={t`travel`} />
            </>
        ),
        stepIcon: 'intelligence',
    }
}

function getRoadFuelInferenceExplanation(
    inferenceDetails: Exclude<InferenceDetails['roadFuel'], null>,
): ExplanationStep {
    const { sourceCountryCode, sourceRegion, value } = inferenceDetails

    const resolveLocation = (region: string | null, countryCode: string | null): string | null => {
        if (region) {
            return region
        }
        if (countryCode) {
            const countryName = getCountryNameByThreeLetterCode(countryCode)
            return countryName ?? countryCode
        }
        // Don't crash if neither is defined, there are production estimates like that.
        // https://linear.app/lune/issue/LUN-4907/dashboard-error-expected-region-or-country-code-to-be-defined-while
        return null
    }

    const noLocationInfo = sourceRegion === null && sourceCountryCode === null
    const geographyParts = (() => {
        if (sourceRegion === 'World' || noLocationInfo) {
            // If we fallback to a global factor, we prefer to say
            // "inferred from shipment route departure" rather than
            // "inferred from shipment route departure: World"
            return null
        }
        const resolved = resolveLocation(sourceRegion, sourceCountryCode)
        if (resolved === null) {
            return null
        }
        return (
            <>
                <InferenceChip text={resolved} />
            </>
        )
    })()

    function formatRoadFuel(value: NonNullable<InferenceDetails['roadFuel']>['value']) {
        const mappings: Record<NonNullable<InferenceDetails['roadFuel']>['value'], string> = {
            gasoline: t`Gasoline`,
            diesel: t`Diesel`,
            '99_diesel_1_biodiesel': t`B1 diesel`,
            '98_diesel_2_biodiesel': t`B2 diesel`,
            '95_diesel_5_biodiesel': t`B5 diesel`,
            '93_diesel_7_biodiesel': t`B7 diesel`,
            '90_diesel_10_biodiesel': t`B10 diesel`,
            '80_diesel_20_biodiesel': t`B20 diesel`,
            '50_diesel_50_biodiesel': t`B50 diesel`,
            ethanol_from_corn: t`Ethanol from corn`,
            hvo_from_tallow: t`Renewable diesel (HVO from tallow)`,
            lpg: t`Liquefied Petroleum Gas (LPG)`,
            cng: t`Compressed natural gas (CNG)`,
            lng: t`Liquefied natural gas (LNG)`,
        }
        return mappings[value]
    }

    return {
        description: (
            <>
                <ExplanationText text={t`Inferred`} />{' '}
                <InferenceChip text={formatRoadFuel(value)} />
                <ExplanationText
                    text={
                        geographyParts !== null
                            ? t`fuel from departure location in`
                            : t`fuel from departure location`
                    }
                />
                {geographyParts}
            </>
        ),
        stepIcon: 'intelligence',
    }
}

function getRoadGradientInferenceExplanation(
    inferenceDetails: Exclude<InferenceDetails['roadGradient'], null>,
): ExplanationStep {
    function formatGradient(value: RoadGradient) {
        const mappings: Record<RoadGradient, string> = {
            flat: t`flat`,
            hilly: t`hilly`,
            mountainous: t`mountainous`,
        }
        return mappings[value]
    }

    return {
        description: (
            <>
                <ExplanationText text={t`Inferred`} />{' '}
                <InferenceChip text={formatGradient(inferenceDetails.value)} />
                <ExplanationText text={t`gradient from shipping route elevation`} />
            </>
        ),
        stepIcon: 'intelligence',
    }
}

function getRoadSituationInferenceExplanation(
    inferenceDetails: Exclude<InferenceDetails['roadSituation'], null>,
): ExplanationStep {
    function formatSituation(value: RoadSituation) {
        const cityUrban = t`city urban`
        const arterialRoad = t`arterial road`
        const motorwayUrban = t`motorway urban`
        const motorwayRural = t`motorway rural`
        const freeflow = t`freeflow`
        const stopGo = t`stop and go`
        const heavyTraffic = t`heavy traffic`

        const mappings: Record<RoadSituation, [string, string]> = {
            city_urban_freeflow: [cityUrban, freeflow],
            city_urban_heavy_traffic: [cityUrban, heavyTraffic],
            city_urban_stop_and_go: [cityUrban, stopGo],
            arterial_road_rural_freeflow: [arterialRoad, freeflow],
            arterial_road_rural_heavy_traffic: [arterialRoad, stopGo],
            arterial_road_rural_stop_and_go: [arterialRoad, heavyTraffic],
            motorway_urban_freeflow: [motorwayUrban, freeflow],
            motorway_urban_heavy_traffic: [motorwayUrban, stopGo],
            motorway_urban_stop_and_go: [motorwayUrban, heavyTraffic],
            motorway_rural_freeflow: [motorwayRural, freeflow],
            motorway_rural_heavy_traffic: [motorwayRural, stopGo],
            motorway_rural_stop_and_go: [motorwayRural, heavyTraffic],
        }
        return mappings[value]
    }

    const formatted = formatSituation(inferenceDetails.value)
    return {
        description: (
            <>
                <ExplanationText text={t`Inferred`} /> <InferenceChip text={formatted[0]} />
                <InferenceChip text={formatted[1]} />
                <ExplanationText
                    text={t`situation from shipment route road types and traffic conditions`}
                />
            </>
        ),
        stepIcon: 'intelligence',
    }
}

function getSeaFuelConsumptionExplanations(
    methodologyDetails: SeaMethodologyDetails,
): ExplanationStep[] {
    return [
        {
            description: (
                <>
                    <ExplanationChip text={t`Modelled voyage fuel consumption`} sx={{ ml: -1 }} />
                    <ExplanationText text={t`at`} />
                    <ExplanationChip
                        text={`${formatNumbers(methodologyDetails.voyageEmissionsPerTeu, 4)} tCO₂e/TEU`}
                    />
                </>
            ),
        },
        {
            description: (
                <Box sx={{ ml: -1 }}>
                    <ExplanationChip
                        text={t`Voyage time: ${formatNumbers(methodologyDetails.voyageTimeHours, 2)} hrs`}
                        icon={<AccessTimeOutlinedIcon />}
                    />
                    <ExplanationChip
                        text={t`Average voyage speed: ${methodologyDetails.vesselCruisingSpeedKnots} kn`}
                        icon={<SpeedOutlinedIcon />}
                    />
                    {methodologyDetails.refrigerationFuelConsumedTonnes ? (
                        <ExplanationChip text={t`Refrigerated`} icon={<AcUnitOutlinedIcon />} />
                    ) : (
                        <></>
                    )}
                    <ExplanationChip
                        sx={methodologyDetails.refrigerationFuelConsumedTonnes ? { mt: 2 } : {}}
                        text={t`Total fuel burned: ${formatNumbers(methodologyDetails.seaVoyageFuelConsumedTonnes + (methodologyDetails.refrigerationFuelConsumedTonnes ?? 0), 2)} t`}
                        icon={<OilBarrelOutlinedIcon />}
                    />
                </Box>
            ),
            stepIcon: 'none',
        },
    ]
}

function getVesselIdentifierExplanation(
    requestMethod: ShippingMethod | LogisticsSiteMethod,
): ExplanationStep {
    if (
        typeof requestMethod === 'string' ||
        !('vesselImoNumber' in requestMethod || 'vesselName' in requestMethod)
    ) {
        throw new Error('Expected at least one vessel identifier')
    }
    return {
        description: (
            <>
                <ExplanationText text={t`Identified the vessel`} />
                <ExplanationChip
                    text={
                        'vesselImoNumber' in requestMethod
                            ? t`Vessel IMO ${requestMethod.vesselImoNumber}`
                            : requestMethod.vesselName
                    }
                />
            </>
        ),
    }
}

export const useExplanationSteps = ({
    rawDistance,
    distance,
    adjustedDistance,
    distanceCalculationMethod,
    distanceCalculationDetails,
    load,
    convertedLoad,
    loadConversionMethod,
    methodology,
    emissionFactor,
    requestMethod,
    requestRoute,
    responseRoute,
    methodologyDetails,
    vesselInference,
    inferenceDetails,
    routeInferenceLegExplanation,
}: EmissionsExplanationSectionProps) => {
    // As of 2025-06-03, adjusted distance has been deprecated
    // We now use raw distance and distance
    // Estimates before this date won't have a raw distance
    if (rawDistance) {
        adjustedDistance = distance
        distance = rawDistance
    }
    const locale = useLuneI18nLocale()

    const distanceExplanation = useMemo(() => {
        return getDistanceExplanation(
            distance,
            adjustedDistance,
            distanceCalculationMethod,
            distanceCalculationDetails,
            responseRoute,
            vesselInference,
            requestRoute,
        )
    }, [
        distance,
        adjustedDistance,
        distanceCalculationMethod,
        distanceCalculationDetails,
        responseRoute,
        vesselInference,
        requestRoute,
        locale,
    ])

    const tradeLaneInferenceExplanation = useMemo(() => {
        return getTradeLaneInferenceExplanation(inferenceDetails?.tradeLaneFromRoute)
    }, [inferenceDetails, locale])

    const railTypeInferenceExplanation = useMemo(() => {
        return inferenceDetails?.railTypeFromRoute
            ? getRailTypeInferenceExplanation(inferenceDetails.railTypeFromRoute)
            : { description: undefined }
    }, [inferenceDetails, locale])

    const roadFuelInferenceExplanation = useMemo(() => {
        return inferenceDetails?.roadFuel
            ? getRoadFuelInferenceExplanation(inferenceDetails.roadFuel)
            : { description: undefined }
    }, [inferenceDetails, locale])

    const roadGradientInferenceExplanation = useMemo(() => {
        return inferenceDetails?.roadGradient
            ? getRoadGradientInferenceExplanation(inferenceDetails.roadGradient)
            : { description: undefined }
    }, [inferenceDetails, locale])

    const roadSituationInferenceExplanation = useMemo(() => {
        return inferenceDetails?.roadSituation
            ? getRoadSituationInferenceExplanation(inferenceDetails.roadSituation)
            : { description: undefined }
    }, [inferenceDetails, locale])

    const roadVehicleTypeInferenceExplanation = useMemo(() => {
        return inferenceDetails?.roadTruckType
            ? getRoadVehicleTypeInferenceExplanation(inferenceDetails.roadTruckType)
            : { description: undefined }
    }, [inferenceDetails, locale])

    const roadEmissionStandardInferenceExplanation = useMemo(() => {
        return inferenceDetails?.roadEmissionStandard
            ? getRoadEmissionStandardInferenceExplanation(inferenceDetails.roadEmissionStandard)
            : { description: undefined }
    }, [inferenceDetails, locale])

    const inferredVesselExplanation = useMemo(
        () =>
            optionFromNullable(vesselInference)
                .andThen((details) => ('details' in details ? Some(details) : None))
                .map(getInferredVesselExplanation),
        [vesselInference, locale],
    )

    const inferredSourceTransferPoint = useMemo(() => {
        if (routeInferenceLegExplanation?.inferenceExplanation.snappedSourceToKnownLocation) {
            return getSnappedToTransferPointExplanation(
                routeInferenceLegExplanation.inferenceExplanation.snappedSourceToKnownLocation,
                'source',
            )
        }
        return null
    }, [routeInferenceLegExplanation, locale])

    const inferredDestinationTransferPoint = useMemo(() => {
        if (routeInferenceLegExplanation?.inferenceExplanation.snappedDestinationToKnownLocation) {
            return getSnappedToTransferPointExplanation(
                routeInferenceLegExplanation.inferenceExplanation.snappedDestinationToKnownLocation,
                'destination',
            )
        }
        return null
    }, [routeInferenceLegExplanation, locale])

    const llmCleanedSource = useMemo(() => {
        if (inferenceDetails?.cleanedRoute?.source) {
            return getLLMCleanedRoute(inferenceDetails.cleanedRoute.source, 'source')
        }
        return null
    }, [inferenceDetails, locale])

    const llmCleanedDestination = useMemo(() => {
        if (inferenceDetails?.cleanedRoute?.destination) {
            return getLLMCleanedRoute(inferenceDetails.cleanedRoute.destination, 'destination')
        }
        return null
    }, [inferenceDetails, locale])

    const shipmentExplanation = useMemo(() => {
        return getShipmentExplanation(load, convertedLoad, loadConversionMethod)
    }, [load, convertedLoad, loadConversionMethod, locale])

    const isDetailedRoadEstimate =
        typeof requestMethod === 'object' &&
        'vehicleType' in requestMethod &&
        'fuel' in requestMethod

    const isElectricityDerivedMethod = !!(
        methodologyDetails && 'electricityConsumption' in methodologyDetails
    )

    const isFuelBasedSeaEstimate = !!(
        methodologyDetails && 'seaVoyageFuelConsumedTonnes' in methodologyDetails
    )

    const formulaExplanation = useMemo(() => {
        return getFormulaExplanation({
            distance,
            isDetailedRoadEstimate,
            isElectricityDerivedMethod,
        })
    }, [distance, isDetailedRoadEstimate, isElectricityDerivedMethod, locale])

    const emissionFactorExplanation = useMemo(() => {
        return getEmissionFactorExplanation(methodology, emissionFactor, {
            basedOnInput: isDetailedRoadEstimate,
        })
    }, [methodology, emissionFactor, isDetailedRoadEstimate, locale])

    if (methodologyDetails && 'flight' in methodologyDetails && methodologyDetails.flight) {
        const flightDetails = methodologyDetails.flight
        return [
            {
                description: distanceExplanation.description,
                info: getAirShipmentDistanceInfo(distance, adjustedDistance),
            },
            getAircraftTypeExplanation(requestMethod, flightDetails),
            shipmentExplanation,
            getFuelUsageExplanation(flightDetails),
            airShipmentFormulaExplanation,
        ].filter((s) => !!s.description)
    }

    if (isFuelBasedSeaEstimate) {
        const vesselExplanation = getVesselIdentifierExplanation(requestMethod)
        const fuelConsumptionExplanations = getSeaFuelConsumptionExplanations(methodologyDetails)
        const formulaExplanation = {
            description: <ExplanationText text={t`Multiplied fuel consumption by load`} />,
        }
        return [
            vesselExplanation,
            distanceExplanation,
            ...fuelConsumptionExplanations,
            shipmentExplanation,
            formulaExplanation,
        ].filter((s) => !!s.description)
    }

    let roadLoadExplanations: ExplanationStep | null = null
    let roadEnergyConsumptionExplanations: ExplanationStep | null = null
    if (isDetailedRoadEstimate) {
        roadLoadExplanations = getRoadLoadExplanations(
            requestMethod,
            methodologyDetails as RoadMethodologyDetails,
        )
        roadEnergyConsumptionExplanations = getRoadEnergyConsuptionExplanation(
            requestMethod,
            methodologyDetails as RoadMethodologyDetails,
        )
    }
    let electricEnergyConsumptionExplanation: ExplanationStep | null = null
    if (isElectricityDerivedMethod) {
        electricEnergyConsumptionExplanation =
            getElectricEnergyConsumptionExplanation(methodologyDetails)
    }

    const roadRefrigerationExplanation = getRoadRefrigerationExplanation(methodologyDetails)

    return [
        inferredSourceTransferPoint,
        inferredDestinationTransferPoint,
        llmCleanedSource,
        llmCleanedDestination,
        distanceExplanation,
        roadFuelInferenceExplanation,
        roadGradientInferenceExplanation,
        roadSituationInferenceExplanation,
        roadVehicleTypeInferenceExplanation,
        roadEmissionStandardInferenceExplanation,
        roadLoadExplanations,
        roadEnergyConsumptionExplanations,
        railTypeInferenceExplanation,
        electricEnergyConsumptionExplanation,
        inferredVesselExplanation.unwrapOr(null),
        tradeLaneInferenceExplanation,
        emissionFactorExplanation,
        shipmentExplanation,
        formulaExplanation,
        roadRefrigerationExplanation,
    ]
        .filter((s): s is Exclude<typeof s, null> => s !== null)
        .filter((s) => !!s.description)
}

export default useExplanationSteps
